import { buildQuery } from '@calendly/http-tools'
import { pick } from 'lodash-es'
import { useCallback } from 'react'

import { pushQueryParams } from 'actions/core/navigation'
import { setFiltersError } from 'actions/meetings/filters'
import { buildSchedulingMethodProps, Periods, Statuses } from 'constants/meetings'
import { useHomeTabs } from 'hooks/home/tabs'
import { useQueryParams } from 'hooks/use_query_params'

const DATE_RANGE_KEYS = [
  'start_date',
  'end_date'
]

const HOST_VALUES = [
  'my_meetings',
  'permission'
]

const TRACKING_KEYS = [
  'utm_campaign',
  'utm_source',
  'utm_medium',
  'utm_content',
  'utm_term'
]

const SECONDARY_FILTER_KEYS = [
  'status_ids',
  'scheduling_methods',
  'user_ids',
  'team_ids',
  'event_type_ids',
  'package_uuids',
  'invitee_emails',
  'utm_campaign',
  'utm_source',
  'utm_medium',
  'utm_content',
  'utm_term'
]

const pickProps = (from, allowlistedProps) => (
  allowlistedProps.reduce((accumulator, currentValue) => {
    accumulator[currentValue] = from[currentValue]
    return accumulator
  }, {})
)

const buildTrackingProps = (params) => (
  TRACKING_KEYS.reduce((accumulator, key) => {
    const value = params[key]
    if (Array.isArray(value)) {
      accumulator[key] = value
    } else {
      // If value is not an array then assume these are UTM params from our marketing team
      accumulator[key] = []
    }
    return accumulator
  }, {})
)

const buildStatusProps = (params) => {
  const props = []
  if (Array.isArray(params.status_ids)) {
    if (params.status_ids.length === 1 && params.status_ids[0] === Statuses.ALL) {
      return params.status_ids
    }
    params.status_ids.forEach((value) => {
      if (Object.values(Statuses).includes(value)) {
        props.push(value)
      }
    })
  }
  if (!props.length) {
    props.push(Statuses.ACTIVE)
  }
  return props
}

const buildHostProps = (params, scope) => {
  const props = []

  if (Array.isArray(params.host_ids)) {
    if (params.host_ids.length === 1 && params.host_ids[0] === 'all') {
      return params.host_ids
    }
    params.host_ids.forEach((value) => {
      if (HOST_VALUES.includes(value)) {
        props.push(value)
      }
    })
  }

  if (scope.type === 'user' && scope.id === 'me' && !props.length) {
    props.push(HOST_VALUES[0])
  }

  return props
}

const buildPeriodProps = (params) => (
  Object.values(Periods).includes(params.period) ? params.period : Periods.UPCOMING
)

const buildFilterParams = (params, scope) => {
  let props = {}
  props.period = buildPeriodProps(params)
  props.status_ids = buildStatusProps(params)
  props.scheduling_methods = buildSchedulingMethodProps(params)
  props.host_ids = buildHostProps(params, scope)
  props = {
    ...props,
    ...pickProps(params, DATE_RANGE_KEYS)
  }
  props.user_ids = params.user_ids || []
  props.team_ids = params.team_ids || []
  props.event_type_ids = params.event_type_ids || []
  props.package_uuids = params.package_uuids || []
  props = {
    ...props,
    ...buildTrackingProps(params)
  }
  if (params.page) {
    props.page = params.page
  }
  props.invitee_emails = params.invitee_emails || []
  props.query = params.query
  props.search_keyword = params.search_keyword
  props.meeting_intelligence_status = params.meeting_intelligence_status === 'COMPLETED' ? 'COMPLETED' : undefined
  // Preserve deletedRecapId query parameter if present
  if (params.deletedRecapId) {
    props.deletedRecapId = params.deletedRecapId
  }

  if (params.status_explicitly_set !== undefined) {
    props.status_explicitly_set = params.status_explicitly_set === 'true' || params.status_explicitly_set === true
  }
  return props
}

const buildItemParams = (filterName, ids) => {
  switch (filterName) {
    case 'status':
    case 'host': {
      const newIds = Object.values(ids).flat().filter((id) => id !== 'all')
      if (!newIds.length) {
        newIds.push('all')
      }
      return {
        [`${filterName}_ids`]: newIds
      }
    }

    case 'tracking': {
      return {
        ...ids
      }
    }

    case 'invitee_email': {
      return {
        [`${filterName}s`]: Object.values(ids).flat()
      }
    }

    default: {
      return {
        [`${filterName}_ids`]: Object.values(ids).flat()
      }
    }
  }
}

export const useFilters = () => {
  const queryParams = useQueryParams()
  const { getTabScopeV2 } = useHomeTabs()
  const tabScope = getTabScopeV2()

  const getFilterParams = useCallback(
    (filterDefaults = {}) => buildFilterParams({ ...filterDefaults, ...queryParams }, tabScope),
    [queryParams, tabScope]
  )

  const applyPeriod = useCallback(
    (type, options = {}) => (dispatch) => {
      const oldParams = buildFilterParams(queryParams, tabScope)
      const params = {
        ...oldParams,
        period: type
      }

      if (options.start_date) {
        params.start_date = options.start_date
      } else {
        delete params.start_date
      }

      if (options.end_date) {
        params.end_date = options.end_date
      } else {
        delete params.end_date
      }

      dispatch(pushQueryParams(params))
    },
    [queryParams, tabScope]
  )

  // low level methods used to apply filter with limit check
  const buildApplyFilterParams = useCallback(
    (filterName, ids) => {
      const oldParams = buildFilterParams(queryParams, tabScope)
      const itemParams = buildItemParams(filterName, ids)
      const newParams = {
        ...oldParams,
        ...itemParams
      }
      delete newParams.page
      return newParams
    },
    [queryParams, tabScope]
  )

  const isValidApplyParams = useCallback(
    (params) => {
      const query = buildQuery(params)
      return query.length < 2000
    },
    []
  )

  const showApplyFilterError = useCallback(
    () => (dispatch) => {
      dispatch(setFiltersError('query_overrun'))
    },
    []
  )

  const applyFilterParams = useCallback(
    (params) => (dispatch) => {
      dispatch(pushQueryParams(params))
    },
    []
  )

  // without limit check
  const applyFilter = useCallback(
    (filterName, ids) => (dispatch) => {
      const newParams = buildApplyFilterParams(filterName, ids)
      dispatch(applyFilterParams(newParams))
    },
    [buildApplyFilterParams, applyFilterParams]
  )

  const isDefaultFilterParams = useCallback(
    (params) => {
      const [statusKey, ...otherKeys] = SECONDARY_FILTER_KEYS

      const isStatusActive = params[statusKey].length === 1 && params[statusKey][0] === 'active'
      const areOtherKeysEmpty = otherKeys.every((key) => params[key].length === 0)

      return isStatusActive && areOtherKeysEmpty
    },
    []
  )

  const resetFilters = useCallback(
    () => (dispatch) => {
      const oldParams = buildFilterParams(queryParams, tabScope)
      const newParams = pick(oldParams, 'period', 'start_date', 'end_date')
      dispatch(pushQueryParams(newParams))
    },
    [queryParams, tabScope]
  )

  const paginateFilters = useCallback(
    (page) => (dispatch) => {
      const oldParams = buildFilterParams(queryParams, tabScope)
      const newParams = {
        ...oldParams,
        page
      }
      dispatch(pushQueryParams(newParams))
    },
    [queryParams, tabScope]
  )

  const resetFilterPagination = useCallback(
    () => (dispatch) => {
      const oldParams = buildFilterParams(queryParams, tabScope)
      const newParams = {
        ...oldParams,
        status_ids: ['all'],
        page: 1
      }
      dispatch(pushQueryParams(newParams))
    },
    [queryParams, tabScope]
  )

  return {
    getFilterParams,
    applyPeriod,
    buildApplyFilterParams,
    isValidApplyParams,
    showApplyFilterError,
    applyFilterParams,
    applyFilter,
    isDefaultFilterParams,
    resetFilters,
    paginateFilters,
    resetFilterPagination
  }
}
